import { BOSS_LAIRS, BOSS_LEVEL_BONUS, MAX_ENEMY_LEVEL } from './constants';
import { ENEMIES, enemyCoinValue, enemyXpValue, spawnTableForLevel, zoneLevelAt } from './enemies';
import type { EnemyKind, PointOfInterest } from './types';

/**
 * Bounties are built from the player's level rather than a fixed list, so there
 * is always something worth taking all the way to level 100.
 *
 * Each bounty names a level band around the player (a level-50 character is
 * asked to kill level 48-53 monsters) and progress only counts kills inside
 * that band. Farming low-level country does nothing, which pairs with the
 * distance-based spawn ramp: the bounty quietly points you outward.
 *
 * Offers are deterministic from the level alone, so the client can work out the
 * board itself and the server only has to validate the accept - no network
 * traffic for a list of jobs.
 */
export interface QuestOffer {
  id: string;
  name: string;
  /** null means "any monster" inside the band. */
  kind: EnemyKind | null;
  count: number;
  minLevel: number;
  maxLevel: number;
  xpReward: number;
  coinReward: number;
  /**
   * Boss bounties target a specific landmark: the index of the lair in the
   * world's point-of-interest list, and where it is, so the client can point an
   * arrow at it.
   */
  bossIndex?: number;
  targetX?: number;
  targetY?: number;
  targetName?: string;
}

export const MAX_ACTIVE_QUESTS = 3;

/** Band around the player's level that a bounty will accept kills from. */
const BAND_BELOW = 2;
const BAND_ABOVE = 3;
/**
 * A bounty pays a fraction of what the kills themselves are worth as bonus XP,
 * plus a healthy coin margin. It rewards the effort without shortcutting the
 * long climb to 100.
 */
const XP_BONUS = 0.3;
const COIN_BONUS = 1.8;
const SPECIFIC_COUNT = 10;
const ANY_COUNT = 18;
const VERBS = ['Cull', 'Hunt', 'Purge'];

function clampLevel(level: number): number {
  return Math.max(1, Math.min(MAX_ENEMY_LEVEL, Math.floor(level)));
}

export function questBand(level: number): { minLevel: number; maxLevel: number } {
  const minLevel = clampLevel(Math.max(1, level - BAND_BELOW));
  const maxLevel = Math.max(minLevel, clampLevel(level + BAND_ABOVE));
  return { minLevel, maxLevel };
}

/** Distinct kinds that actually spawn at this level, strongest last. */
function kindChoices(level: number): EnemyKind[] {
  const table = spawnTableForLevel(clampLevel(level));
  const out: EnemyKind[] = [];
  for (const entry of table) {
    if (!out.includes(entry.kind)) out.push(entry.kind);
  }
  return out;
}

/** Weighted-average base XP of what lives at this level, for the "any" bounty. */
function averageXpBase(level: number): number {
  const table = spawnTableForLevel(clampLevel(level));
  let weight = 0;
  let sum = 0;
  for (const entry of table) {
    weight += entry.weight;
    sum += entry.weight * ENEMIES[entry.kind].xpBase;
  }
  return weight > 0 ? sum / weight : 15;
}

/**
 * A landmark boss whose level is close to the player's, if one exists. Bosses
 * lair at fixed landmarks, so a boss bounty sends the player to a place rather
 * than a vague stretch of wilderness.
 */
function bossOffer(level: number, landmarks: PointOfInterest[]): QuestOffer | null {
  const target = clampLevel(level);
  let best: { poi: PointOfInterest; index: number; bossLevel: number } | null = null;

  landmarks.forEach((poi, index) => {
    if (!BOSS_LAIRS[poi.kind]) return;
    const bossLevel = clampLevel(zoneLevelAt(poi.x, poi.y) + BOSS_LEVEL_BONUS);
    if (Math.abs(bossLevel - target) > 3) return;
    if (!best || Math.abs(bossLevel - target) < Math.abs(best.bossLevel - target)) {
      best = { poi, index, bossLevel };
    }
  });

  if (!best) return null;
  const chosen = best as { poi: PointOfInterest; index: number; bossLevel: number };
  const bossName = BOSS_LAIRS[chosen.poi.kind];

  return {
    id: `boss:${chosen.index}:${chosen.bossLevel}`,
    name: bossName,
    kind: null,
    count: 1,
    minLevel: chosen.bossLevel,
    maxLevel: chosen.bossLevel,
    xpReward: Math.round(enemyXpValue(ENEMIES.boss, chosen.bossLevel) * 0.5),
    coinReward: Math.round(enemyCoinValue(ENEMIES.boss, chosen.bossLevel) * 1.5),
    bossIndex: chosen.index,
    targetX: Math.round(chosen.poi.x),
    targetY: Math.round(chosen.poi.y),
    targetName: chosen.poi.name,
  };
}

/** The bounty board for a character of this level. */
export function offersFor(level: number, landmarks: PointOfInterest[] = []): QuestOffer[] {
  const target = clampLevel(level);
  const { minLevel, maxLevel } = questBand(level);
  const kinds = kindChoices(level);
  const offers: QuestOffer[] = [];

  // A boss hunt, when a suitable lair is in range, leads the board.
  const boss = bossOffer(level, landmarks);
  if (boss) offers.push(boss);

  kinds.slice(0, 2).forEach((kind, index) => {
    const def = ENEMIES[kind];
    offers.push({
      id: `bounty:${kind}:${minLevel}`,
      name: `${def.name} ${VERBS[index % VERBS.length]}`,
      kind,
      count: SPECIFIC_COUNT,
      minLevel,
      maxLevel,
      xpReward: Math.round(enemyXpValue(def, target) * SPECIFIC_COUNT * XP_BONUS),
      coinReward: Math.round(enemyCoinValue(def, target) * SPECIFIC_COUNT * COIN_BONUS),
    });
  });

  // A catch-all for the current band, worth more because it is broader.
  const fallback = ENEMIES[kinds[0] ?? 'slime'];
  const averaged = { ...fallback, xpBase: averageXpBase(level) };
  offers.push({
    id: `bounty:any:${minLevel}`,
    name: 'Cull the Wilds',
    kind: null,
    count: ANY_COUNT,
    minLevel,
    maxLevel,
    xpReward: Math.round(enemyXpValue(averaged, target) * ANY_COUNT * XP_BONUS),
    coinReward: Math.round(enemyCoinValue(averaged, target) * ANY_COUNT * COIN_BONUS),
  });

  return offers;
}

export function questBlurb(offer: QuestOffer): string {
  if (offer.bossIndex !== undefined) {
    return `Hunt ${offer.name} (Lv ${offer.minLevel}) at the ${offer.targetName}. Follow the arrow.`;
  }
  const band = `level ${offer.minLevel}-${offer.maxLevel}`;
  if (offer.kind) {
    const enemy = ENEMIES[offer.kind];
    const plural = offer.count === 1 ? enemy.name : `${enemy.name}s`;
    return `Slay ${offer.count} ${plural} of ${band} out in the wilds.`;
  }
  return `Slay ${offer.count} monsters of ${band} out in the wilds.`;
}
